import { useDashboardFilters } from 'App/dashboards/_utils/dashboardFiltersContext'
import { hasPestDataAccess, type TPropertyOption } from 'App/dashboards/_utils/useDashboardPropertyOptions'
import moment from 'moment-timezone'
import { useCallback, useMemo, useRef } from 'react'
import { dashboardsStore } from 'stores/dashboardsStore/dashboardsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'

// YTD + 2 weeks so the forecast tail is visible by default (matches the date picker's +14d maxDate).
const getDefaultDateRange = () => {
  const timezone = moment.tz.guess()

  return {
    from: moment.tz(timezone).startOf('year'),
    to: moment.tz(timezone).add(14, 'days').endOf('day'),
  }
}

export type TDashboardFilters = {
  // Property options narrowed to this dashboard's pest.
  options: TPropertyOption[]
  selectedPropertyIds: string[]
  onPropertyChange: (values: string[]) => void
  dateFrom: moment.Moment
  dateTo: moment.Moment
  onDateRangeChange: (from: moment.Moment, to: moment.Moment) => void
}

/**
 * Per-dashboard property + date range, persisted to the dashboardsStore (ionic storage) under the
 * dashboard's stable id. Landing order: saved preference (if still valid) → the map's selected
 * property (if valid for this pest) → first option. The auto-resolved fallback is NOT persisted;
 * only an explicit user change writes back, per spec ("once the user selects, we save it").
 */
export const useDashboardPreferences = ({
  dashboardId,
  insectId,
}: {
  dashboardId: string
  insectId: number
}): TDashboardFilters => {
  const { propertyOptions } = useDashboardFilters()
  const selectedPropertyFromMap = selectedFieldAssetsStore.useSelector((s) => s.property)
  const preference = dashboardsStore.useSelector((s) => s.preferencesByDashboardId[dashboardId])

  // Same per-pest narrowing rule as the section list (degree-day OR trap-catch access).
  const options = useMemo(
    () => propertyOptions.filter((o) => hasPestDataAccess(Number(o.value), insectId)),
    [propertyOptions, insectId],
  )

  const selectedPropertyId = useMemo(() => {
    const valid = new Set(options.map((o) => o.value))

    if (preference?.propertyId && valid.has(preference.propertyId)) return preference.propertyId

    const mapId = selectedPropertyFromMap === null ? null : String(selectedPropertyFromMap)

    if (mapId && valid.has(mapId)) return mapId

    return options[0]?.value ?? null
  }, [options, preference?.propertyId, selectedPropertyFromMap])

  // Stable per-mount default so unchanged renders don't churn the chart's date-based fetch deps.
  const defaults = useRef(getDefaultDateRange())
  const timezone = moment.tz.guess()

  // Normalised to whole local days on read as well as on write. A range persisted before the picker
  // started normalising ends at 00:00 on its end date, which silently drops that day from anything
  // read at an hourly or finer interval. These are no-ops for a correctly stored range.
  const dateFrom = useMemo(
    () =>
      preference?.dateFrom ? moment.tz(preference.dateFrom, timezone).startOf('day') : defaults.current.from,
    [preference?.dateFrom, timezone],
  )

  const dateTo = useMemo(
    () => (preference?.dateTo ? moment.tz(preference.dateTo, timezone).endOf('day') : defaults.current.to),
    [preference?.dateTo, timezone],
  )

  // Every write persists the full preference, filling unchanged fields from the current effective
  // values — so changing only the date still captures the resolved property, and vice-versa.
  const onPropertyChange = useCallback(
    (values: string[]) => {
      const propertyId = values[0] ?? null

      dashboardsStore.setState((s) => ({
        ...s,
        preferencesByDashboardId: {
          ...s.preferencesByDashboardId,
          [dashboardId]: { propertyId, dateFrom: dateFrom.toISOString(), dateTo: dateTo.toISOString() },
        },
      }))
    },
    [dashboardId, dateFrom, dateTo],
  )

  const onDateRangeChange = useCallback(
    (from: moment.Moment, to: moment.Moment) => {
      dashboardsStore.setState((s) => ({
        ...s,
        preferencesByDashboardId: {
          ...s.preferencesByDashboardId,
          [dashboardId]: {
            propertyId: selectedPropertyId,
            dateFrom: from.toISOString(),
            dateTo: to.toISOString(),
          },
        },
      }))
    },
    [dashboardId, selectedPropertyId],
  )

  return useMemo(
    () => ({
      options,
      selectedPropertyIds: selectedPropertyId ? [selectedPropertyId] : [],
      onPropertyChange,
      dateFrom,
      dateTo,
      onDateRangeChange,
    }),
    [options, selectedPropertyId, onPropertyChange, dateFrom, dateTo, onDateRangeChange],
  )
}
